/**
 * The app's navigation, as data.
 *
 * One list drives the sidebar, the page headers and the tutorial's ordering
 * test, so a tool is added, renamed or moved in exactly one place. The
 * sidebar used to be fourteen hand-written buttons in App.tsx, each with its own
 * accent colour, and the tutorial test had to regex them out of the source.
 *
 * Every item needs its own icon: when the sidebar is collapsed the icon is all
 * an editor sees. A test fails on a repeated icon.
 */

import {
    Bitcoin,
    BookOpen,
    FileText,
    Image as ImageIcon,
    LayoutList,
    Newspaper,
    Radio,
    Scale,
    Search,
    Sunrise,
    Tags,
    TrendingUp,
    Zap,
    type LucideIcon,
} from 'lucide-react'

import { TUTORIAL } from './tutorial/content'
import { TOOL_VIEW, type ToolId, type ToolView } from './tutorial/types'

/** Every page the app can show. */
export type View = ToolView | 'tutorial'

export interface NavItem {
    view: View
    /** As in the sidebar and the page header. The tutorial's titles must match. */
    label: string
    icon: LucideIcon
}

export interface NavGroup {
    /** Shown above the group when the sidebar is expanded. */
    label: string
    items: NavItem[]
}

export const DEFAULT_VIEW: View = 'article-generator'

export const NAV_GROUPS: NavGroup[] = [
    {
        label: 'Überblick & automatisch',
        items: [
            { view: 'breaking-news', label: 'Breaking News', icon: Zap },
            { view: 'morning-briefing', label: 'Morning Briefing', icon: Sunrise },
            { view: 'infographics', label: 'News-Grafiken', icon: ImageIcon },
            { view: 'newsflash', label: 'Newsflash', icon: Radio },
        ],
    },
    {
        label: 'Artikel',
        items: [
            { view: 'article-generator', label: 'Article Generator', icon: Newspaper },
        ],
    },
    {
        label: 'Kurzmeldungen',
        items: [
            { view: 'headlines-generator', label: 'Headlines Generator', icon: LayoutList },
            { view: 'crypto-headlines-generator', label: 'Crypto Headlines', icon: Bitcoin },
            { view: 'single-headline', label: 'Single Headline', icon: FileText },
        ],
    },
    {
        label: 'Quartalszahlen',
        items: [{ view: 'revenue-generator', label: 'Revenue News', icon: TrendingUp }],
    },
    {
        label: 'Prüfen & anreichern',
        items: [
            { view: 'formalia-check', label: 'Formalia Check', icon: Scale },
            { view: 'seo-tool', label: 'SEO Optimization', icon: Tags },
            { view: 'find-similar', label: 'Find Similar Links', icon: Search },
        ],
    },
    {
        label: 'Hilfe',
        items: [{ view: 'tutorial', label: 'Tutorial', icon: BookOpen }],
    },
]

export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items)

const BY_VIEW = new Map<View, NavItem>(NAV_ITEMS.map((item) => [item.view, item]))

const TOOL_BY_VIEW = new Map<View, ToolId>(
    (Object.entries(TOOL_VIEW) as [ToolId, ToolView][]).map(([tool, view]) => [view, tool]),
)

export function isView(value: string): value is View {
    return BY_VIEW.has(value as View)
}

export function navItem(view: View): NavItem {
    const item = BY_VIEW.get(view)
    if (!item) throw new Error(`No navigation entry for view "${view}"`)
    return item
}

/**
 * The one-line description under a page's title: the tool's tagline from the
 * (German) tutorial, so the header and the guide never describe a tool in two
 * different ways.
 */
export function viewDescription(view: View): string {
    const tool = TOOL_BY_VIEW.get(view)
    return tool ? TUTORIAL.de.tools[tool].tagline : TUTORIAL.de.ui.subtitle
}
